import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';
import { buscarConferencia, registrarLeitura, listarLeituras } from '../../../services/conferencia';
import type { Conferencia, ConferenciaLeitura, ResumoLeituras } from '../../../types/conferencia';
import '../Conferencia.css';

const ConferenciaScanner: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [conferencia, setConferencia] = useState<Conferencia | null>(null);
  const [resumoLeituras, setResumoLeituras] = useState<ResumoLeituras | null>(null);
  const [leituras, setLeituras] = useState<ConferenciaLeitura[]>([]);
  const [codigo, setCodigo] = useState('');
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState<{
    tipo: 'ok' | 'duplicado' | 'nao_encontrado' | 'erro';
    mensagem: string;
  } | null>(null);
  const [tipoLeitura, setTipoLeitura] = useState<string>('codigo_barras');
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scanningCameraRef = useRef<boolean>(false);
  const animationFrameRef = useRef<number | null>(null);

  const carregarDados = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [confResult, leitResult] = await Promise.all([
        buscarConferencia(parseInt(id)),
        listarLeituras(parseInt(id), { limit: 50 })
      ]);
      if (confResult.success) {
        setConferencia(confResult.conferencia);
        setResumoLeituras(confResult.resumo_leituras);
      }
      if (leitResult.success) {
        setLeituras(leitResult.leituras);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { carregarDados(); }, [carregarDados]);
  useEffect(() => { if (!loading) inputRef.current?.focus(); }, [loading]);

  // Referência para o formulário para auto-submit após leitura da câmera
  const formRef = useRef<HTMLFormElement>(null);

  // Gerenciamento da câmera para leitura de código de barras em tempo real
  useEffect(() => {
    if (!showCamera) return;

    let mounted = true;
    let activeStream: MediaStream | null = null;
    let barcodeInterval: ReturnType<typeof setInterval> | null = null;

    const onDetected = (data: string) => {
      if (!mounted) return;
      scanningCameraRef.current = false;
      setCodigo(data);
      setShowCamera(false);
      // Auto-submit após leitura
      setTimeout(() => formRef.current?.requestSubmit(), 100);
    };

    const scanFrame = () => {
      if (!scanningCameraRef.current || !mounted || !videoRef.current || !canvasRef.current) return;

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx || !video.videoWidth || !video.videoHeight) {
        animationFrameRef.current = requestAnimationFrame(scanFrame);
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      });

      if (code?.data) {
        onDetected(code.data);
        return;
      }

      if (scanningCameraRef.current && mounted) {
        animationFrameRef.current = requestAnimationFrame(scanFrame);
      }
    };

    const initCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        });

        activeStream = stream;

        if (!mounted || !videoRef.current) {
          stream.getTracks().forEach(t => t.stop());
          activeStream = null;
          return;
        }

        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        scanningCameraRef.current = true;
        scanFrame();

        // Usar BarcodeDetector API para códigos de barras 1D (quando disponível)
        if ('BarcodeDetector' in window) {
          barcodeInterval = setInterval(async () => {
            if (!scanningCameraRef.current || !videoRef.current || !mounted) return;
            try {
              const detector = new (window as any).BarcodeDetector();
              const barcodes = await detector.detect(videoRef.current);
              if (barcodes.length > 0 && scanningCameraRef.current && mounted) {
                onDetected(barcodes[0].rawValue);
              }
            } catch { /* silenciar erros do detector */ }
          }, 500);
        }
      } catch {
        if (mounted) {
          setCameraError('Não foi possível acessar a câmera. Verifique as permissões do navegador.');
        }
      }
    };

    initCamera();

    return () => {
      mounted = false;
      scanningCameraRef.current = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (barcodeInterval) clearInterval(barcodeInterval);
      if (activeStream) {
        activeStream.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCamera]);

  const handleScan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !codigo.trim() || scanning || conferenciaFinalizada) return;

    setScanning(true);
    setLastResult(null);

    try {
      const result = await registrarLeitura(parseInt(id), {
        tipo_leitura: tipoLeitura,
        codigo_lido: codigo.trim(),
        quantidade: 1
      });

      if (result.success) {
        if (result.duplicado) {
          setLastResult({ tipo: 'duplicado', mensagem: 'Código já lido anteriormente' });
        } else if (result.encontrado) {
          setLastResult({ tipo: 'ok', mensagem: result.item?.descricao || 'Item conferido com sucesso' });
        } else {
          setLastResult({ tipo: 'nao_encontrado', mensagem: 'Código não encontrado na lista de itens' });
        }
        await carregarDados();
      }
    } catch (error: any) {
      setLastResult({ tipo: 'erro', mensagem: error.response?.data?.message || 'Erro ao processar leitura' });
    } finally {
      setCodigo('');
      setScanning(false);
      inputRef.current?.focus();
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  if (!conferencia) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#7f8c8d' }}>
          <p>Conferência não encontrada</p>
        </div>
      </div>
    );
  }

  const progresso = conferencia.total_itens_esperado > 0
    ? Math.round((conferencia.total_itens_conferido / conferencia.total_itens_esperado) * 100)
    : 0;

  const conferenciaFinalizada = conferencia.status === 'concluida' || conferencia.status === 'cancelada';

  const getLeituraIcon = (resultado: string): string => {
    const map: Record<string, string> = {
      ok: 'fas fa-check-circle', duplicado: 'fas fa-copy',
      nao_encontrado: 'fas fa-times-circle', avaria: 'fas fa-exclamation-triangle', erro: 'fas fa-times-circle',
      removido: 'fas fa-trash'
    };
    return map[resultado] || 'fas fa-question-circle';
  };

  const getLeituraColor = (resultado: string): string => {
    const map: Record<string, string> = {
      ok: '#27ae60', duplicado: '#f39c12', nao_encontrado: '#e74c3c', avaria: '#e67e22', erro: '#e74c3c',
      removido: '#95a5a6'
    };
    return map[resultado] || '#7f8c8d';
  };

  const getLeituraLabel = (resultado: string): string => {
    const map: Record<string, string> = {
      ok: 'OK', duplicado: 'Duplicado', nao_encontrado: 'Não Encontrado', avaria: 'Avaria', erro: 'Erro',
      removido: 'Removido'
    };
    return map[resultado] || resultado;
  };

  const getStatusClass = (resultado: string): string => {
    const map: Record<string, string> = {
      ok: 'status-completed', duplicado: 'status-maintenance', nao_encontrado: 'status-delayed', avaria: 'status-delayed', erro: 'status-delayed',
      removido: 'status-inactive'
    };
    return map[resultado] || 'status-inactive';
  };

  return (
    <div className="page-container" style={{ maxWidth: 900, margin: '0 auto' }}>
      <button className="conferencia-back-link" onClick={() => navigate(`/conferencia/${id}`)}>
        <i className="fas fa-arrow-left"></i> Voltar para conferência
      </button>

      {/* Aviso de conferência finalizada */}
      {conferenciaFinalizada && (
        <div className="conferencia-alert warning">
          <i className="fas fa-exclamation-triangle" style={{ color: '#f39c12' }}></i>
          <div>
            <p className="alert-title" style={{ color: '#856404' }}>Conferência {conferencia.status === 'concluida' ? 'concluída' : 'cancelada'}</p>
            <p className="alert-message">Não é possível registrar novas leituras nesta conferência.</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body">
          <h1 style={{ fontSize: '1.5rem', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="fas fa-barcode"></i> Scanner - Conferência #{conferencia.id}
          </h1>
          <div className="conferencia-progress">
            <div className="progress-info">
              <span>{conferencia.total_itens_conferido} de {conferencia.total_itens_esperado} itens</span>
              <span>{progresso}%</span>
            </div>
            <div className="progress-bar">
              <div className={`progress-fill ${progresso >= 100 ? 'complete' : ''}`} style={{ width: `${Math.min(progresso, 100)}%` }} />
            </div>
          </div>
        </div>
      </div>

      {/* Área de Leitura */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body">
          <form ref={formRef} onSubmit={handleScan}>
            <div className="scanner-input-wrapper">
              <select className="form-control" value={tipoLeitura} onChange={(e) => setTipoLeitura(e.target.value)} style={{ width: 'auto' }}>
                <option value="codigo_barras">Código de Barras</option>
                <option value="qrcode">QR Code</option>
                <option value="manual">Manual</option>
              </select>
              <div className="scanner-field">
                <i className="fas fa-barcode scanner-field-icon-left"></i>
                <input
                  ref={inputRef}
                  type="text"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  placeholder="Leia ou digite o código..."
                  autoFocus
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="scanner-camera-btn"
                  onClick={() => { setCameraError(null); setShowCamera(true); }}
                  disabled={conferenciaFinalizada}
                  title="Abrir câmera para escanear"
                >
                  <i className="fas fa-camera"></i>
                </button>
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!codigo.trim() || scanning || conferenciaFinalizada}
              >
                <i className="fas fa-check"></i> {scanning ? 'Lendo...' : 'Conferir'}
              </button>
            </div>
          </form>

          {/* Resultado da última leitura */}
          {lastResult && (
            <div className={`scanner-result ${lastResult.tipo === 'ok' ? 'success' : lastResult.tipo === 'duplicado' ? 'warning' : 'error'}`}>
              <i className={`${lastResult.tipo === 'ok' ? 'fas fa-check-circle' : lastResult.tipo === 'duplicado' ? 'fas fa-copy' : lastResult.tipo === 'nao_encontrado' ? 'fas fa-times-circle' : 'fas fa-exclamation-triangle'}`}
                style={{ color: lastResult.tipo === 'ok' ? '#27ae60' : lastResult.tipo === 'duplicado' ? '#f39c12' : '#e74c3c' }}></i>
              <div>
                <p className="result-title" style={{ color: lastResult.tipo === 'ok' ? '#1b5e20' : lastResult.tipo === 'duplicado' ? '#856404' : '#721c24' }}>
                  {lastResult.tipo === 'ok' ? 'Conferido!' : lastResult.tipo === 'duplicado' ? 'Duplicado' : lastResult.tipo === 'nao_encontrado' ? 'Não Encontrado' : 'Erro'}
                </p>
                <p className="result-message">{lastResult.mensagem}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Resumo de Leituras */}
      {resumoLeituras && (
        <div className="conferencia-stats-grid" style={{ marginBottom: 20 }}>
          <div className="conferencia-stat-card" style={{ textAlign: 'center' }}>
            <p className="stat-value">{resumoLeituras.total_leituras}</p>
            <span style={{ fontSize: '0.75rem', color: '#7f8c8d' }}>Total</span>
          </div>
          <div className="conferencia-stat-card" style={{ textAlign: 'center' }}>
            <p className="stat-value" style={{ color: '#27ae60' }}>{resumoLeituras.leituras_ok}</p>
            <span style={{ fontSize: '0.75rem', color: '#7f8c8d' }}>OK</span>
          </div>
          <div className="conferencia-stat-card" style={{ textAlign: 'center' }}>
            <p className="stat-value" style={{ color: '#f39c12' }}>{resumoLeituras.duplicados}</p>
            <span style={{ fontSize: '0.75rem', color: '#7f8c8d' }}>Duplicados</span>
          </div>
          <div className="conferencia-stat-card" style={{ textAlign: 'center' }}>
            <p className="stat-value" style={{ color: '#e74c3c' }}>{resumoLeituras.nao_encontrados}</p>
            <span style={{ fontSize: '0.75rem', color: '#7f8c8d' }}>Não Encontrados</span>
          </div>
          <div className="conferencia-stat-card" style={{ textAlign: 'center' }}>
            <p className="stat-value" style={{ color: '#e67e22' }}>{resumoLeituras.avarias}</p>
            <span style={{ fontSize: '0.75rem', color: '#7f8c8d' }}>Avarias</span>
          </div>
        </div>
      )}

      {/* Histórico de Leituras */}
      <div className="card">
        <div className="card-header">
          <h2>Últimas Leituras</h2>
        </div>
        <div style={{ maxHeight: 384, overflowY: 'auto' }}>
          {leituras.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#7f8c8d' }}>Nenhuma leitura realizada</div>
          ) : (
            leituras.map(leitura => (
              <div key={leitura.id} className="leitura-item" style={{ borderBottom: '1px solid #ecf0f1' }}>
                <div className="leitura-info">
                  <i className={getLeituraIcon(leitura.resultado)} style={{ color: getLeituraColor(leitura.resultado) }}></i>
                  <div>
                    <span className="leitura-code">{leitura.codigo_lido}</span>
                    <p className="leitura-time">
                      {new Date(leitura.lido_em).toLocaleString('pt-BR')}
                      {leitura.usuario_nome && ` - ${leitura.usuario_nome}`}
                    </p>
                  </div>
                </div>
                <span className={`status-badge ${getStatusClass(leitura.resultado)}`}>
                  {getLeituraLabel(leitura.resultado)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal da câmera para escanear código de barras */}
      {showCamera && (
        <div className="scanner-camera-modal-overlay">
          <div className="scanner-camera-modal">
            <div className="scanner-camera-modal-header">
              <h4>
                <i className="fas fa-camera" style={{ marginRight: '8px', color: '#04364F' }}></i>
                Escanear Código
              </h4>
              <button onClick={() => setShowCamera(false)} className="scanner-camera-modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="scanner-camera-viewport">
              <video
                ref={videoRef}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                playsInline
                muted
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />

              {/* Overlay de escaneamento */}
              {!cameraError && (
                <div className="scanner-camera-crosshair" />
              )}
            </div>

            {cameraError ? (
              <div className="scanner-camera-error">
                <i className="fas fa-exclamation-triangle" style={{ marginRight: '6px' }}></i>
                {cameraError}
              </div>
            ) : (
              <p className="scanner-camera-hint">
                <i className="fas fa-crosshairs" style={{ marginRight: '6px' }}></i>
                Aponte a câmera para o QR Code ou código de barras
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConferenciaScanner;
